'use strict';

/* eslint-disable @typescript-eslint/no-var-requires */

// Google Cloud Storage resumable-upload protocol. The backend issues the
// create-only initiation URL; the browser owns chunk transfer and session
// cancellation because the returned session URI is a bearer capability.

const {
  UploadHttpError,
  fetchWithRetry,
  reportProgress,
  responseError,
  uploadErrorMessage,
} = require('./core');

const MIN_GCS_CHUNK = 1_048_576;
const MAX_GCS_CHUNK = 1_048_576 * 64;
const GCS_CHUNK_GRANULARITY = 256 * 1024;

async function initiateGcsSession(upload, options) {
  let signedUrl = upload.record.signedUrl;
  const refreshAuthority = async () => {
    const authorization = await upload.api.authorize(upload.record.id, []);
    signedUrl = authorization.signedUrl;
    if (!signedUrl) {
      throw new Error('GCS did not return resumable upload authorization.');
    }
  };
  if (!signedUrl) await refreshAuthority();

  const response = await fetchWithRetry(
    () =>
      options.fetchImpl(signedUrl, {
        method: 'POST',
        headers: {
          'X-Goog-Resumable': 'start',
          // This signed header makes object creation fail if the key exists.
          'X-Goog-If-Generation-Match': '0',
          'Content-Type': 'application/octet-stream',
          'X-Upload-Content-Length': String(upload.file.size),
        },
        mode: 'cors',
        signal: options.signal,
      }),
    {
      ...options,
      operation: 'Starting the GCS resumable upload',
      accepted: (candidate) => candidate.ok,
      refreshAuthority,
    }
  );

  const sessionUri = response.headers.get('Location');
  if (!sessionUri) {
    throw new Error(
      'GCS did not expose the resumable session URI. Check bucket CORS headers.'
    );
  }

  // Persist the opaque session URI immediately so a page reload can resume it.
  upload.record.sessionUri = sessionUri;
  await reportProgress(
    upload,
    upload.api,
    upload.onProgress,
    0,
    false,
    options.signal
  );
  return sessionUri;
}

function gcsChunkSize(fileSize) {
  const target = Math.min(
    Math.max(fileSize / 10, MIN_GCS_CHUNK),
    MAX_GCS_CHUNK
  );
  // Every non-final GCS chunk must be a multiple of 256 KiB.
  return Math.floor(target / GCS_CHUNK_GRANULARITY) * GCS_CHUNK_GRANULARITY;
}

function nextGcsOffset(response, fallbackOffset) {
  const range = response.headers.get('Range');
  const match = range ? /bytes=\d+-(\d+)/.exec(range) : null;
  return match ? Number.parseInt(match[1], 10) + 1 : fallbackOffset;
}

function isGcsEmulatorSession(sessionUri) {
  try {
    const sessionUrl = new URL(sessionUri);
    return (
      ['localhost', '127.0.0.1', '[::1]'].includes(
        sessionUrl.hostname.toLowerCase()
      ) && sessionUrl.pathname.startsWith('/upload/storage/v1/b/')
    );
  } catch (_error) {
    return false;
  }
}

async function uploadGcsSession(upload, sessionUri, options) {
  const fileSize = upload.file.size;
  let startByte = 0;

  // fake-gcs-server finalizes an empty object on a status query. Skip that
  // emulator-only behavior, while keeping normal resume probes for real GCS.
  if (!isGcsEmulatorSession(sessionUri)) {
    const statusResponse = await fetchWithRetry(
      () =>
        options.fetchImpl(sessionUri, {
          method: 'PUT',
          headers: { 'Content-Range': `bytes */${fileSize}` },
          mode: 'cors',
          signal: options.signal,
        }),
      {
        ...options,
        operation: 'Querying the GCS resumable upload',
        accepted: (candidate) => candidate.ok || candidate.status === 308,
      }
    );
    if (statusResponse.ok) {
      await reportProgress(
        upload,
        upload.api,
        upload.onProgress,
        fileSize,
        false,
        options.signal
      );
      return;
    }
    startByte = nextGcsOffset(statusResponse, 0);
  }

  await reportProgress(
    upload,
    upload.api,
    upload.onProgress,
    startByte,
    false,
    options.signal
  );
  const chunkSize = gcsChunkSize(fileSize);

  while (startByte < fileSize) {
    const endByte = Math.min(startByte + chunkSize, fileSize) - 1;
    const body = upload.file.slice(startByte, endByte + 1);
    const chunkResponse = await fetchWithRetry(
      () =>
        options.fetchImpl(sessionUri, {
          method: 'PUT',
          headers: {
            'Content-Range': `bytes ${startByte}-${endByte}/${fileSize}`,
            'Content-Type': 'application/octet-stream',
          },
          body,
          mode: 'cors',
          signal: options.signal,
        }),
      {
        ...options,
        operation: `Uploading GCS bytes ${startByte}-${endByte}`,
        accepted: (candidate) => candidate.ok || candidate.status === 308,
      }
    );

    startByte = chunkResponse.ok
      ? fileSize
      : nextGcsOffset(chunkResponse, endByte + 1);
    await reportProgress(
      upload,
      upload.api,
      upload.onProgress,
      startByte,
      false,
      options.signal
    );
  }
}

async function runGcsResumable(upload, options) {
  let sessionUri = upload.record.sessionUri;

  // A GCS session can expire between page visits. Replace it once; a second
  // expiry is surfaced instead of creating an unbounded restart loop.
  for (let restart = 0; restart < 2; restart += 1) {
    if (!sessionUri) sessionUri = await initiateGcsSession(upload, options);
    try {
      await uploadGcsSession(upload, sessionUri, options);
      return;
    } catch (error) {
      const expired =
        error instanceof UploadHttpError &&
        (error.status === 404 || error.status === 410);
      if (!expired || restart > 0) throw error;
      const authorization = await upload.api.authorize(upload.record.id, []);
      upload.record.signedUrl = authorization.signedUrl;
      upload.record.sessionUri = null;
      sessionUri = await initiateGcsSession(upload, options);
    }
  }
}

async function cancelGcsResumableSession(upload, api, request) {
  try {
    // Reauthorization asks the backend to reconcile DELETE CORS for legacy
    // buckets. It returns the existing resumable session rather than replacing it.
    const authorization = await api.authorize(upload.id, []);
    const sessionUri = authorization.sessionUri || upload.sessionUri;
    const response = await request(sessionUri, {
      method: 'DELETE',
      mode: 'cors',
    });

    // Missing/expired sessions are already safely cancelled. GCS JSON may use
    // 499 for a successful resumable-session cancellation.
    if (!response.ok && ![404, 410, 499].includes(response.status)) {
      throw await responseError(
        response,
        'Cancelling the GCS resumable session'
      );
    }
  } catch (error) {
    // Fail closed: retaining RUNNING plus the session URI permits a later retry;
    // marking ABORTED here would lose the only cleanup capability.
    throw new Error(
      `GCS session cleanup could not be confirmed; retry cancellation: ${uploadErrorMessage(
        error
      )}`
    );
  }
}

module.exports = { cancelGcsResumableSession, runGcsResumable };
